/**
 * AI feature flags from Vite env (`import.meta.env`). Requires a rebuild to change toggles.
 * See [`docs/ai-feature-flags.md`](../../docs/ai-feature-flags.md) for variable names.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { AiFeature } from '@/lib/ai-features-flag';
import {
  notifyAiFeaturesUnavailable,
  readAiFeatureFlagsFromImportMeta,
} from '@/lib/ai-features-flag';

interface AiFeatureFlagsContextValue {
  masterEnabled: boolean;
  features: Record<AiFeature, boolean>;
  loading: boolean;
  error: Error | null;
  /** True when all AI is allowed and this feature is not turned off. */
  isFeatureEnabled: (feature: AiFeature) => boolean;
  /** Toast + return false when disabled. */
  ensureFeatureEnabled: (feature: AiFeature) => boolean;
  refetch: () => Promise<void>;
}

const AiFeatureFlagsContext = createContext<AiFeatureFlagsContextValue | null>(
  null
);

export function AiFeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { masterEnabled, features } = useMemo(
    () => readAiFeatureFlagsFromImportMeta(import.meta.env),
    []
  );

  const isFeatureEnabled = useCallback(
    (feature: AiFeature): boolean => {
      if (!masterEnabled) return false;
      return features[feature] !== false;
    },
    [masterEnabled, features]
  );

  const ensureFeatureEnabled = useCallback(
    (feature: AiFeature): boolean => {
      if (isFeatureEnabled(feature)) return true;
      notifyAiFeaturesUnavailable();
      return false;
    },
    [isFeatureEnabled]
  );

  const refetch = useCallback(async () => {}, []);

  const value = useMemo<AiFeatureFlagsContextValue>(
    () => ({
      masterEnabled,
      features,
      loading: false,
      error: null,
      isFeatureEnabled,
      ensureFeatureEnabled,
      refetch,
    }),
    [
      masterEnabled,
      features,
      isFeatureEnabled,
      ensureFeatureEnabled,
      refetch,
    ]
  );

  return (
    <AiFeatureFlagsContext.Provider value={value}>
      {children}
    </AiFeatureFlagsContext.Provider>
  );
}

export function useAiFeatureFlags(): AiFeatureFlagsContextValue {
  const ctx = useContext(AiFeatureFlagsContext);
  if (!ctx) {
    throw new Error('useAiFeatureFlags must be used within AiFeatureFlagsProvider');
  }
  return ctx;
}
